import type { ReactNode } from 'react'
import { Alert, Anchor, Badge, Group, Skeleton, Stack, Table, Text } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { SectionCard, StatusBadge } from '@lighthouse/design'
import { entityOutcomePresentation } from '../execution-history/executionFormat'
import { useExecution } from '../../hooks/useExecutions'
import { agoLabel, exactNumber, whenLabel } from '../../utils/format'
import {
  SAME_LEVEL_CAPTION, aloneAtLevelText, higherLevelsLead, levelsIntro, nothingBelowText, runLevels, unresolvedKeysText,
} from './levelsFormat'
import type { EtlRunDetail } from '../../types/etlRuns'
import type { ExecutionDetail, ExecutionEntity } from '../../types/executions'

/**
 * The ETL Explorer's Dependencies tab: this entity's LOAD LEVEL among the rest
 * of its run, captioned as levels because no dependency graph is recorded.
 *
 * The other entities come from Execution History's run detail
 * (`useExecution(run.executionId)`), so this is the one place on the Explorer
 * that reads that endpoint — and it is mounted only while the tab is open, so
 * no other tab pays for the request. Everything it decides lives in
 * levelsFormat.ts; this file only lays it out.
 */

interface RunLevelsPanelProps {
  run: EtlRunDetail
  /**
   * How a failed read is shown: the page's LoadError, by status. Passed in so a
   * 401, a 500 and an unreachable API read here exactly as they do on the
   * page's other tabs.
   */
  renderError: (error: unknown) => ReactNode
}

export function RunLevelsPanel({ run, renderError }: RunLevelsPanelProps) {
  const { data, isLoading, isError, error, dataUpdatedAt } = useExecution(run.executionId)

  let body: ReactNode
  if (isError && !data) {
    body = renderError(error)
  } else if (isLoading || !data) {
    body = <Skeleton height={240} />
  } else {
    body = <Levels run={run} execution={data} refreshFailedAt={isError ? dataUpdatedAt : null} />
  }

  return (
    <SectionCard title="Load levels" subtitle="Not a dependency graph — none is recorded.">
      <Stack gap="md">
        <Text size="sm">{levelsIntro(run)}</Text>
        <Text size="sm">{unresolvedKeysText(run.records)}</Text>
        {body}
      </Stack>
    </SectionCard>
  )
}

function Levels({ run, execution, refreshFailedAt }: {
  run: EtlRunDetail
  execution: ExecutionDetail
  refreshFailedAt: number | null
}) {
  const levels = runLevels(run, execution)
  const n = levels.level

  return (
    <Stack gap="md">
      {refreshFailedAt !== null && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} title="Could not refresh">
          Showing what was last loaded, {agoLabel(new Date(refreshFailedAt).toISOString())}.
        </Alert>
      )}

      {levels.scope && <Text size="sm">{levels.scope}</Text>}

      {levels.notes.length > 0 && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} title="These lists may be incomplete">
          <Stack gap={4}>
            {levels.notes.map((note) => <Text key={note} size="sm">{note}</Text>)}
          </Stack>
        </Alert>
      )}

      <Stack gap="xs">
        <Text size="sm" fw={600}>Below level {n}</Text>
        {levels.lower.length === 0
          ? <Text size="sm" c="dimmed">{nothingBelowText(n)}</Text>
          : levels.lower.map((group) => (
            <Stack key={group.level} gap={4}>
              <Text size="xs" fw={600} c="dimmed">Level {group.level}</Text>
              <EntityTable rows={group.entities} selfId={null} />
            </Stack>
          ))}
      </Stack>

      <Stack gap="xs">
        <Text size="sm" fw={600}>Level {n}</Text>
        <Text size="xs" c="dimmed">{SAME_LEVEL_CAPTION}</Text>
        {(levels.self || levels.sameLevel.length > 0) && (
          <EntityTable
            rows={levels.self ? [levels.self, ...levels.sameLevel] : levels.sameLevel}
            selfId={levels.self?.entityRunId ?? null}
          />
        )}
        {levels.sameLevel.length === 0 && <Text size="sm" c="dimmed">{aloneAtLevelText(n)}</Text>}
      </Stack>

      <Text size="sm">
        {higherLevelsLead(levels.higherCount)} — see run{' '}
        <Anchor component={Link} to={`/execution-history/${run.executionId}`} size="sm" ff="monospace">
          {run.runKey}
        </Anchor>
        .
      </Text>
    </Stack>
  )
}

/** One level's rows. `selfId` marks this entity's own row, which is shown but not linked: it is this page. */
function EntityTable({ rows, selfId }: { rows: ExecutionEntity[]; selfId: string | null }) {
  return (
    <Table.ScrollContainer minWidth={560}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Entity</Table.Th>
            <Table.Th>Outcome</Table.Th>
            <Table.Th>Started</Table.Th>
            <Table.Th ta="right">Attempts</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((entity) => (
            <EntityRow key={entity.entityRunId} entity={entity} isSelf={entity.entityRunId === selfId} />
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  )
}

function EntityRow({ entity, isSelf }: { entity: ExecutionEntity; isSelf: boolean }) {
  const look = entityOutcomePresentation(entity.outcome)
  return (
    <Table.Tr aria-current={isSelf || undefined}>
      <Table.Td>
        {isSelf
          ? (
            <Group gap={6} wrap="nowrap">
              <Text size="sm" fw={600}>{entity.entityName}</Text>
              <Badge color="gray" variant="outline" size="xs" radius="sm">this run</Badge>
            </Group>
          )
          : (
            <Anchor component={Link} to={`/etl-explorer/${entity.entityRunId}`} size="sm">
              {entity.entityName}
            </Anchor>
          )}
        <Text size="xs" c="dimmed">{entity.module ?? 'No module recorded'}</Text>
      </Table.Td>
      <Table.Td>
        {look.role
          ? <StatusBadge status={look.role} label={look.label} />
          : <Badge color="gray" variant="light" radius="sm">{look.label}</Badge>}
      </Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap' }}>{whenLabel(entity.startedAt)}</Table.Td>
      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{exactNumber(entity.attemptCount)}</Table.Td>
    </Table.Tr>
  )
}
